import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/api";
import { Box, Button, Typography, Divider } from "@mui/material";

const StaffProfilePage = () => {
  const { id } = useParams(); // Получаем ID сотрудника из URL
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const navigate = useNavigate();

  const handleEditClick = () => {
    navigate(`/staff/edit/${id}`); // Переход на страницу редактирования
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setStaff(null);
    setError('');
    const fetchStaff = async () => {
      try {
        const response = await api.get(`/staff/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (active) setStaff(response.data && typeof response.data === 'object' && !Array.isArray(response.data) ? response.data : null);
      } catch (error) {
        if (active && error.response?.status !== 404) setError('Не удалось загрузить сотрудника.');
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchStaff();
    return () => { active = false; };
  }, [id, retry]);

  if (loading) {
    return <Typography>Загрузка...</Typography>;
  }

  if (!staff) {
    return error ? <Box><Typography>{error}</Typography><Button onClick={() => setRetry(value => value + 1)}>Повторить</Button></Box>
      : <Typography>Сотрудник не найден.</Typography>;
  }

  const fullName = [staff.last_name, staff.first_name, staff.middle_name].filter(Boolean).join(' ') || staff.full_name || "Имя не указано";
  const dateOnly = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T|$)/.test(value) && !Number.isNaN(new Date(value).getTime()) ? value.split('T')[0] : '-';

  return (
    <Box
      sx={{
        padding: "20px",
        maxWidth: "100%", // Контейнер занимает 80% ширины страницы
        margin: "0 auto", // Центрирование на странице
      }}
    >
      {/* Основной блок с общей информацией */}
      <Box
        sx={{
          padding: 2,
          border: "1px solid #ccc",
          borderRadius: "12px",
          backgroundColor: "#ffffff",
          marginBottom: "20px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 2,
          }}
        >
          <Button onClick={() => window.history.back()} variant="outlined">
            Назад
          </Button>

          <Button variant="contained" color="primary" onClick={handleEditClick}>
            Редактировать
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 2,
          }}
        >
          <img
            src={staff.image || "https://via.placeholder.com/150"}
            alt={fullName}
            style={{
              width: "150px",
              height: "150px",
              borderRadius: "8px",
              objectFit: "cover",
            }}
          />
          <Box>
            <Typography variant="h5">
              {fullName}
            </Typography>
            <Typography color="textSecondary">{staff.role}</Typography>
            <Typography>Телефон: {staff.phone || "Не указан"}</Typography>
            <Typography>Почта: {staff.email}</Typography>
            <Typography>
              Пункты проката: {staff.rental_point?.name || "-"}
            </Typography>
            <Typography>Адрес: {staff.address || "-"}</Typography>
            <Typography>
              Дата рождения: {dateOnly(staff.date_of_birth)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Блок с паспортной информацией */}
      <Box
        sx={{
          display: "flex", // Устанавливаем флекс-контейнер
          gap: "20px", // Расстояние между блоками
          flexWrap: "wrap", // Если места не хватает, блоки переносятся на следующую строку
          justifyContent: "space-between", // Равномерное распределение по ширине
        }}
      >
        {/* Блок Паспорт */}
        <Box
          sx={{
            padding: 2,
            border: "1px solid #ccc",
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            flex: "1 1 30%", // Блок занимает 30% от ширины контейнера
            minWidth: "300px", // Минимальная ширина блока
          }}
        >
          <Typography variant="h6">Паспорт</Typography>
          <Divider sx={{ marginY: "10px" }} />
          <Typography>Серия: {staff.passport_series || "-"}</Typography>
          <Typography>Номер: {staff.passport_number || "-"}</Typography>
          <Typography>
            Дата выдачи: {dateOnly(staff.passport_issue_date)}
          </Typography>
          <Typography>
            Код подразделения: {staff.passport_division_code || "-"}
          </Typography>
        </Box>

        {/* Блок Текущая смена */}
        <Box
          sx={{
            padding: 2,
            border: "1px solid #ccc",
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            flex: "1 1 30%", // Блок занимает 30% от ширины контейнера
            minWidth: "300px", // Минимальная ширина блока
          }}
        >
          <Typography variant="h6">Текущая смена</Typography>
          <Divider sx={{ marginY: "10px" }} />
          <Typography>Начало смены: {staff.shift_start || "-"}</Typography>
          <Typography>
            Длительность смены: {staff.shift_duration || "-"}
          </Typography>
        </Box>

        {/* Блок Финансы */}
        <Box
          sx={{
            padding: 2,
            border: "1px solid #ccc",
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            flex: "1 1 30%", // Блок занимает 30% от ширины контейнера
            minWidth: "300px", // Минимальная ширина блока
          }}
        >
          <Typography variant="h6">Финансы</Typography>
          <Divider sx={{ marginY: "10px" }} />
          <Typography>Аренда: {staff.total_rent || "0"} ₽</Typography>
          <Typography>Продажи: {staff.total_sales || "0"} ₽</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default StaffProfilePage;
